import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Avatar,
  HStack,
  VStack,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import { FiChevronDown } from 'react-icons/fi';

import { useGetUserBrandsQuery } from '../../Redux/features/auth/authApiSlice';
import { selectSelectedBrandId } from '../../Redux/features/auth/authSlice';
import { useBrandSelector } from '../../Hooks/useBrandSelector';

export default function BrandSelector() {
  const selectedBrandId = useSelector(selectSelectedBrandId);
  const { data: userBrands, isLoading, error } = useGetUserBrandsQuery();
  const { switchBrand, setDefaultBrand } = useBrandSelector();
  const toast = useToast();

  // Auto-seleccionar la primera marca si no hay ninguna seleccionada
  useEffect(() => {
    // Verificación adicional para asegurar que userBrands existe y tiene data
    if (userBrands && userBrands.data && userBrands.data.length && !selectedBrandId) {
      setDefaultBrand(userBrands.data[0]._id);
    }
  }, [userBrands, selectedBrandId, setDefaultBrand]);

  // Encontrar la marca actualmente seleccionada, o usar la primera como fallback
  const selectedBrand = userBrands?.data?.find(brand => brand._id === selectedBrandId) 
    || userBrands?.data?.[0]; // Usar primera marca como fallback

  const handleBrandChange = (brandId, brandName) => {
    switchBrand(brandId);
    toast({
      title: `Cambiado a ${brandName}`,
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" mr={4}>
        <Spinner size="sm" mr={2} />
        <Text fontSize="sm">Cargando marcas...</Text>
      </Box>
    );
  }

  if (error || !userBrands?.data?.length) {
    return null; // No mostrar nada si hay error o no hay marcas
  }

  // Si solo hay una marca, NO mostrar NADA
  if (userBrands.data.length === 1) {
    return null;
  }

  // Si hay múltiples marcas, mostrar selector completo
  return (
    <Menu>
      <MenuButton
        as={Box}
        cursor="pointer"
        bg="#f7fafc"
        border="1px solid #e2e8f0"
        borderRadius="8px"
        px={3}
        py={2}
        mr={4}
        transition="all 0.2s"
        _hover={{ bg: '#edf2f7' }}
      >
        <HStack spacing={2}>
          <Avatar
            size="xs"
            src={selectedBrand?.logoUrl || 'https://i.ibb.co/bK5fhrs/placeholder.png'}
          />
          <VStack spacing={0} alignItems="flex-start">
            <Text fontSize="xs" color="gray.500" lineHeight={1}>
              Marca actual
            </Text>
            <Text fontSize="sm" fontWeight="medium" lineHeight={1}>
              {selectedBrand?.name || 'Seleccionar marca'}
            </Text>
          </VStack>
          <FiChevronDown size={14} color="#666" />
        </HStack>
      </MenuButton>
      
      <MenuList minW="250px">
        <Box px={3} py={2} borderBottom="1px solid #e2e8f0">
          <Text fontSize="xs" color="gray.500" fontWeight="bold">
            CAMBIAR MARCA
          </Text>
        </Box>
        {userBrands.data.map((brand) => (
          <MenuItem
            key={brand._id}
            onClick={() => handleBrandChange(brand._id, brand.name)}
            bg={brand._id === selectedBrandId ? '#edf2f7' : 'transparent'}
            _hover={{ bg: '#f7fafc' }}
          >
            <HStack spacing={3} w="100%">
              <Avatar
                size="sm"
                src={brand.logoUrl || 'https://i.ibb.co/bK5fhrs/placeholder.png'}
              />
              <VStack spacing={0} alignItems="flex-start" flex={1}>
                <Text fontSize="sm" fontWeight="medium">
                  {brand.name}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {brand.email}
                </Text>
              </VStack>
              {brand._id === selectedBrandId && (
                <Box
                  w={2}
                  h={2}
                  bg="green.500"
                  borderRadius="full"
                />
              )}
            </HStack>
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
} 